import { useArtifactCacheStore } from '@/stores/artifactCacheStore'
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

export interface ArtifactTypeCoverage {
  total: ComputedRef<number>
  unlinked: ComputedRef<number>
  linked: ComputedRef<number>
  unlinkedPercent: ComputedRef<number>
  linkedPercent: ComputedRef<number>
  tooltipText: ComputedRef<string>
}

/**
 * Composable to compute link coverage for an artifact type based on its
 * `artifactCount` and `unlinkedArtifactCount` fields returned by the backend.
 *
 * @param artifactTypeIdSource - MaybeRefOrGetter that resolves to the artifact type ID
 */
export function useArtifactTypeCoverage(
  artifactTypeIdSource: MaybeRefOrGetter<number>,
): ArtifactTypeCoverage {
  const artifactCacheStore = useArtifactCacheStore()

  const artifactType = computed(() => {
    const id = toValue(artifactTypeIdSource)
    return artifactCacheStore.artifactTypes.find((at) => at.id === id)
  })

  const total = computed(() => artifactType.value?.artifactCount ?? 0)
  const unlinked = computed(() => artifactType.value?.unlinkedArtifactCount ?? 0)
  const linked = computed(() => Math.max(0, total.value - unlinked.value))

  const unlinkedPercent = computed(() =>
    total.value > 0 ? (unlinked.value / total.value) * 100 : 0,
  )
  const linkedPercent = computed(() => (total.value > 0 ? (linked.value / total.value) * 100 : 0))

  const tooltipText = computed(() => {
    if (total.value === 0) return 'No artifacts'
    return `Linked: ${linked.value} | Unlinked: ${unlinked.value} (of ${total.value})`
  })

  return { total, unlinked, linked, unlinkedPercent, linkedPercent, tooltipText }
}
